// Orenza Button — primary, ghost, danger variants per BRAND_GUIDE.md §6.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

// v1.6.0 — added `subtle` and `link`. An audit found 133 raw <button> elements
// bypassing this component, and the two biggest reasons were a missing quiet
// secondary style and a missing text-link style, so every page hand-rolled them
// and they drifted apart. Adding the variants is what makes consistency achievable
// rather than merely requested.
export type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'warm' | 'subtle' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xs';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
}

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-caption',
  sm: 'h-8 px-3 text-body-sm',
  md: 'h-10 px-5 text-body-md',
  lg: 'h-12 px-6 text-body-lg',
};

// Design System v2. The primary carries the SIGNATURE indigo→violet gradient — it is
// the same gesture as the page-header rail, so the main action on every screen ties
// back to the brand. That is the "lively, strong first impression" made systematic
// rather than sprinkled.
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'text-white [background-image:var(--brand-grad-135)] shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_2px_8px_-2px_rgba(79,70,229,0.5)] ' +
    'hover:brightness-[1.07] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_4px_14px_-2px_rgba(79,70,229,0.55)] ' +
    'active:brightness-95 disabled:bg-cool disabled:[background-image:none] disabled:text-white disabled:shadow-none',
  ghost:
    'bg-transparent text-obsidian border border-mist hover:bg-haze hover:border-cobalt/40 active:bg-mist disabled:text-cool',
  danger:
    'bg-crimson text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:brightness-110 active:brightness-95 disabled:bg-cool',
  // Secondary solid — sharp violet, for a second-tier action that still needs weight.
  warm:
    'bg-violet text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:brightness-110 active:brightness-95 disabled:bg-cool',
  subtle:
    'bg-haze text-obsidian hover:bg-mist active:bg-mist/80 disabled:text-cool',
  link:
    'bg-transparent text-cobalt hover:underline underline-offset-2 disabled:text-cool !h-auto !px-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', leftIcon, rightIcon, loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-[filter,background-color] select-none',
        'disabled:cursor-not-allowed',
        // One focus ring for every button in the app. Keyboard users previously got
        // whatever the browser default was on 133 raw buttons — often nothing visible
        // against a near-white surface.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cobalt focus-visible:ring-offset-2 focus-visible:ring-offset-snow',
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      ) : (
        leftIcon
      )}
      <span>{children}</span>
      {!loading && rightIcon}
    </button>
  );
});
